import { valueAt } from "./table";
import abbreviations from "./tables/abbreviations.json";
import ages from "./tables/ages.json";
import blocks from "./tables/blocks.json";
import categories from "./tables/categories.json";
import confusables from "./tables/confusables.json";
import scripts from "./tables/scripts.json";

// One code point and everything the page can say about it without asking for a name: the four properties Unicode
// publishes as ranges, the abbreviation that is the only thing anybody calls a character nothing draws, and the
// ASCII it can be taken for. Where it starts in the text is kept because a finding names positions and not letters.
export interface Character {
  code: number;
  text: string;
  at: number;
  category: string;
  script: string;
  block: string;
  age: string;
  abbreviation: string;
  looksLike: string;
}

// A text is a list of code points and never of UTF-16 units: an emoji is one character to whoever typed it, and the
// two halves it is stored as are a fact about the storage that the encodings card is where to read.
export function readCharacters(text: string): Character[] {
  const found: Character[] = [];
  let at = 0;
  for (const character of text) {
    found.push(readCharacter(character.codePointAt(0) ?? 0, at));
    at += character.length;
  }
  return found;
}

// One code point read on its own, which is what the keyboard's keys are: a character nobody has typed yet, and so
// one with nowhere in a text to be.
export function readCharacter(code: number, at = 0): Character {
  return {
    code,
    text: String.fromCodePoint(code),
    at,
    category: valueAt(categories, code),
    // Unicode writes a script with underscores where the words go; nothing here is read by anything but a reader.
    script: valueAt(scripts, code).replace(/_/g, " "),
    block: valueAt(blocks, code),
    age: valueAt(ages, code),
    abbreviation: ABBREVIATIONS[hex(code)] ?? "",
    looksLike: CONFUSABLES[hex(code)] ?? "",
  };
}

// The two-letter category as the standard spells it out, which is the half of `Lu` anybody but a regular expression
// is reading. Fixed by the standard rather than published as data, so it is written here and not generated.
export function categoryName(category: string): string {
  return CATEGORY_NAMES[category] ?? category;
}

// A character with nothing to draw: a control, a format character, any separator including the space bar's own,
// half of a surrogate pair, a private use code point nobody here has the font for, and one that is not a character.
// The space is in this and out of `risks.ts`'s own reading of the same word: a blank cell in a table of characters
// says nothing, and a text with a space in it is not a text with anything hidden in it.
export function isInvisible({ category }: Character): boolean {
  return INVISIBLE_CATEGORIES.has(category);
}

// What the character column draws for one of those: the abbreviation where Unicode publishes one, since ZWSP and
// NBSP are what these are called, and a placeholder where it does not.
export function placeholder(character: Character): string {
  return character.abbreviation || PLACEHOLDER;
}

export function codePoint(code: number): string {
  return `U+${hex(code)}`;
}

function hex(code: number): string {
  return code.toString(16).toUpperCase().padStart(4, "0");
}

const PLACEHOLDER = "▯";

const INVISIBLE_CATEGORIES = new Set(["Cc", "Cf", "Cn", "Co", "Cs", "Zl", "Zp", "Zs"]);

const CATEGORY_NAMES: Record<string, string> = {
  Lu: "Uppercase letter",
  Ll: "Lowercase letter",
  Lt: "Titlecase letter",
  Lm: "Modifier letter",
  Lo: "Other letter",
  Mn: "Non-spacing mark",
  Mc: "Spacing combining mark",
  Me: "Enclosing mark",
  Nd: "Decimal digit",
  Nl: "Letter number",
  No: "Other number",
  Pc: "Connector punctuation",
  Pd: "Dash punctuation",
  Ps: "Opening punctuation",
  Pe: "Closing punctuation",
  Pi: "Initial quotation",
  Pf: "Final quotation",
  Po: "Other punctuation",
  Sm: "Maths symbol",
  Sc: "Currency symbol",
  Sk: "Modifier symbol",
  So: "Other symbol",
  Zs: "Space separator",
  Zl: "Line separator",
  Zp: "Paragraph separator",
  Cc: "Control",
  Cf: "Format",
  Cs: "Surrogate",
  Co: "Private use",
  Cn: "Unassigned",
};

// Both are keyed by the code point spelled as four hex digits, which is how the generator writes them down.
const ABBREVIATIONS: Record<string, string> = abbreviations;
const CONFUSABLES: Record<string, string> = confusables;
